import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { uniqBy } from "ramda";
import { AppResource } from "../../../../types/resource.types";

import { IChat, IRoomHeader, IRooms } from "../../type";

type GenericObject = Record<string, any>;

const initialState: IChat = {
  hasNotification: false,
  latestUnreadMessage: undefined,
  loadingRooms: false,
  loadingMessages: false,
  loadingSendMessage: false,
  messageSent: false,
  roomsHeader: [],
  rooms: {},
  messages: {},
};

const chatSlice = createSlice({
  name: "@__chat",
  initialState,
  reducers: {
    setChatNotification(state: IChat, action: PayloadAction<any>) {
      state.hasNotification = !!action.payload;
      state.latestUnreadMessage = action.payload?.lastMessage;
    },
    setChatRoomsLoading(state: IChat, action: PayloadAction<boolean>) {
      if (action.payload !== state.loadingRooms) state.loadingRooms = action.payload;
    },
    setChatRoomMessagesLoading(state: IChat, action: PayloadAction<boolean>) {
      if (action.payload !== state.loadingMessages) state.loadingMessages = action.payload;
    },
    setChatRooms(state: IChat, action: PayloadAction<{ roomsHeader: IRoomHeader[]; rooms: IRooms }>) {
      state.roomsHeader = action.payload.roomsHeader;
      state.rooms = action.payload.rooms;
    },
    syncChatRooms(state: IChat, action: PayloadAction<GenericObject>) {
      const room = action.payload;
      const currentRoomsHeader = state.roomsHeader;
      currentRoomsHeader.unshift({
        roomId: room.id,
        lastMessageUpdate: room.updatedAt,
      });
      state.roomsHeader = uniqBy((roomHeader: IRoomHeader) => roomHeader.roomId)(currentRoomsHeader);
      state.rooms[room.id] = room;
    },
    setChatRoomMessages(state: IChat, action: PayloadAction<{ roomId: string; messages: GenericObject[] }>) {
      const roomId = action.payload.roomId;
      if (roomId !== AppResource.rooms) state.messages[action.payload.roomId] = action.payload.messages;
    },
    loadMoreRoomMessages(state: IChat, action: PayloadAction<{ roomId: string; messages: GenericObject[] }>) {
      const roomId = action.payload.roomId;
      if (roomId !== AppResource.rooms) {
        const currentMessages = state.messages[action.payload.roomId];
        state.messages[action.payload.roomId] = [...currentMessages, ...action.payload.messages];
      }
    },
    syncRoomMessage(state: IChat, action: PayloadAction<{ roomId: string; message: GenericObject }>) {
      const roomId = action.payload.roomId;
      if (roomId !== AppResource.rooms) {
        const roomMessages = state.messages[action.payload.roomId];
        const newMessage = action.payload.message;
        const isMessageExist = roomMessages.find((message: GenericObject) => message.id === newMessage.id);
        if (!isMessageExist) roomMessages.unshift(newMessage);
      }
    },
    markRoomAsSeen(state: IChat, action: PayloadAction<{ roomId: string; userId: string }>) {
      state.rooms[action.payload.roomId].seenBy[action.payload.userId] = true;
    },
    setSendMessageLoading(state: IChat, action: PayloadAction<boolean>) {
      if (action.payload !== state.loadingSendMessage) state.loadingSendMessage = action.payload;
    },
    setMessageSent(state: IChat, action: PayloadAction<boolean>) {
      if (action.payload !== state.messageSent) state.messageSent = action.payload;
    },
  },
});

export const {
  setChatNotification,
  setChatRoomsLoading,
  setChatRoomMessagesLoading,
  setChatRooms,
  setChatRoomMessages,
  loadMoreRoomMessages,
  syncChatRooms,
  markRoomAsSeen,
  syncRoomMessage,
} = chatSlice.actions;
export default chatSlice.reducer;
